import { useState } from 'react';
import { FiTrash, FiDownload, FiUpload } from 'react-icons/fi';
import { useDiagram } from '../../contexts/DiagramContext';
import { exportDiagrams, importDiagrams, deleteDiagram } from '../../services/storageService';
import './SavedDiagrams.css';

const SavedDiagrams = () => {
  const { savedDiagrams, loadDiagram, currentDiagramId } = useDiagram();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importError, setImportError] = useState(null);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Handle diagram selection
  const handleSelectDiagram = (id) => {
    loadDiagram(id);
  };

  // Handle diagram deletion
  const handleDeleteDiagram = async (id) => {
    if (confirmDelete === id) {
      try {
        await deleteDiagram(id);
        setConfirmDelete(null);
        // Reload the page to refresh the diagrams list
        window.location.reload();
      } catch (error) {
        console.error('Error deleting diagram:', error);
      }
    } else {
      setConfirmDelete(id);
    }
  };

  // Handle export all diagrams
  const handleExportAll = async () => {
    try {
      const jsonString = await exportDiagrams();
      
      // Create a blob from the JSON string
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mermaid-diagrams.json';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting diagrams:', error);
    }
  };

  // Handle import diagrams
  const handleImport = () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'application/json';
    
    // Handle file selection
    fileInput.onchange = async (event) => {
      try {
        const file = event.target.files[0];
        if (!file) return;
        
        // Read the file
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const jsonString = e.target.result;
            await importDiagrams(jsonString);
            
            // Reload the page to refresh the diagrams list
            window.location.reload();
          } catch (error) {
            console.error('Error importing diagrams:', error);
            setImportError('Invalid diagram data format');
          }
        };
        reader.readAsText(file);
      } catch (error) {
        console.error('Error reading file:', error);
        setImportError('Error reading file');
      }
    };
    
    // Trigger the file input
    fileInput.click();
  };

  return (
    <div className="saved-diagrams">
      <div className="saved-diagrams-header">
        <h2>Saved Diagrams</h2>
        
        <div className="saved-diagrams-actions">
          <button 
            className="btn btn-secondary btn-icon" 
            onClick={handleExportAll}
            title="Export All Diagrams"
            disabled={savedDiagrams.length === 0}
          >
            <FiDownload size={16} />
          </button>
          
          <button 
            className="btn btn-secondary btn-icon" 
            onClick={handleImport}
            title="Import Diagrams"
          >
            <FiUpload size={16} />
          </button>
        </div>
      </div>
      
      {importError && (
        <div className="error-message">
          {importError}
          <button 
            className="close-button" 
            onClick={() => setImportError(null)}
          >
            &times;
          </button>
        </div>
      )}
      
      {savedDiagrams.length === 0 ? (
        <div className="empty-state">
          <p>No saved diagrams yet</p>
        </div>
      ) : (
        <div className="saved-diagrams-list">
          {savedDiagrams.map((diagram) => (
            <div 
              key={diagram.id} 
              className={`saved-diagram-item ${diagram.id === currentDiagramId ? 'active' : ''}`}
            >
              <div 
                className="saved-diagram-info"
                onClick={() => handleSelectDiagram(diagram.id)}
              >
                <h3>{diagram.title}</h3>
                <p className="saved-diagram-date">
                  Updated: {formatDate(diagram.updatedAt)}
                </p>
              </div>
              
              <button 
                className="btn btn-icon btn-danger" 
                onClick={() => handleDeleteDiagram(diagram.id)}
                title="Delete Diagram"
              >
                {confirmDelete === diagram.id ? '✓' : <FiTrash size={16} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedDiagrams; 